import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface NPSResultsProps {
  responses: number[]; // Tableau des scores (0-10)
}

/**
 * Composant NPSResults
 * Calcule et affiche le score NPS avec segments
 * NPS = % Promoteurs - % Détracteurs
 */
export function NPSResults({ responses }: NPSResultsProps) {
  const { t } = useTranslation("forms");

  // Agrégation mémoïsée en une seule passe (Phase 3A prod-readiness) — évite
  // les ~14 .filter() sur tout le tableau à chaque render. useMemo placé avant
  // tout return conditionnel (rules-of-hooks).
  const {
    detractors,
    passives,
    promoters,
    total,
    detractorsPercent,
    passivesPercent,
    promotersPercent,
    npsScore,
    distribution,
  } = useMemo(() => {
    const total = responses.length;
    const dist = Array.from({ length: 11 }, (_, i) => ({
      score: i,
      count: 0,
      percent: 0,
    }));
    let det = 0;
    let pas = 0;
    let pro = 0;
    for (const s of responses) {
      if (s >= 0 && s <= 10) dist[s].count++;
      if (s <= 6) det++;
      else if (s <= 8) pas++;
      else pro++;
    }
    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
    for (const d of dist) d.percent = pct(d.count);
    const detractorsPercent = pct(det);
    const promotersPercent = pct(pro);
    return {
      detractors: det,
      passives: pas,
      promoters: pro,
      total,
      detractorsPercent,
      passivesPercent: pct(pas),
      promotersPercent,
      npsScore: promotersPercent - detractorsPercent,
      distribution: dist,
    };
  }, [responses]);

  if (responses.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg text-center">
        <p className="text-gray-500">
          {t("forms.results.noResponsesYetShort")}
        </p>
      </div>
    );
  }

  // Interprétation du score
  const getInterpretation = (score: number) => {
    if (score >= 70)
      return {
        label: t("nps.interpretation.excellent"),
        color: "text-green-600",
        bg: "bg-green-100",
      };
    if (score >= 50)
      return {
        label: t("nps.interpretation.veryGood"),
        color: "text-green-600",
        bg: "bg-green-100",
      };
    if (score >= 30)
      return {
        label: t("nps.interpretation.good"),
        color: "text-green-600",
        bg: "bg-green-50",
      };
    if (score >= 0)
      return {
        label: t("nps.interpretation.acceptable"),
        color: "text-yellow-600",
        bg: "bg-yellow-50",
      };
    if (score >= -30)
      return {
        label: t("nps.interpretation.toImprove"),
        color: "text-orange-600",
        bg: "bg-orange-50",
      };
    return {
      label: t("nps.interpretation.critical"),
      color: "text-red-600",
      bg: "bg-red-50",
    };
  };

  const interpretation = getInterpretation(npsScore);

  return (
    <div className="space-y-6" data-testid="nps-results">
      {/* Score NPS principal */}
      <div
        className={`p-6 rounded-xl ${interpretation.bg} border-2 border-${interpretation.color.replace("text-", "")}`}
        data-testid="nps-score-display"
      >
        <div className="text-center">
          <p className="text-sm font-medium text-gray-600 mb-2">
            {t("nps.scoreTitle")}
          </p>
          <div className={`text-6xl font-bold ${interpretation.color}`}>
            {npsScore > 0 ? "+" : ""}
            {npsScore}
          </div>
          <p className={`text-lg font-semibold mt-2 ${interpretation.color}`}>
            {interpretation.label}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t("forms.results.rating.responsesCount", { count: total })}
          </p>
        </div>
      </div>

      {/* Segments */}
      <div className="grid grid-cols-3 gap-4">
        {/* Détracteurs */}
        <div
          className="p-4 bg-red-50 rounded-lg border border-red-200"
          data-testid="nps-detractors"
        >
          <div className="text-center">
            <div className="w-4 h-4 bg-red-500 rounded-full mx-auto mb-2"></div>
            <p className="text-2xl font-bold text-red-600">
              {detractorsPercent}%
            </p>
            <p className="text-sm font-medium text-red-700 mt-1">
              {t("nps.legend.detractors")}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {detractors} / {total}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("nps.segment.scoreRange0_6")}
            </p>
          </div>
        </div>

        {/* Passifs */}
        <div
          className="p-4 bg-yellow-50 rounded-lg border border-yellow-200"
          data-testid="nps-passives"
        >
          <div className="text-center">
            <div className="w-4 h-4 bg-yellow-500 rounded-full mx-auto mb-2"></div>
            <p className="text-2xl font-bold text-yellow-600">
              {passivesPercent}%
            </p>
            <p className="text-sm font-medium text-yellow-700 mt-1">
              {t("nps.legend.passives")}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {passives} / {total}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("nps.segment.scoreRange7_8")}
            </p>
          </div>
        </div>

        {/* Promoteurs */}
        <div
          className="p-4 bg-green-50 rounded-lg border border-green-200"
          data-testid="nps-promoters"
        >
          <div className="text-center">
            <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-2"></div>
            <p className="text-2xl font-bold text-green-600">
              {promotersPercent}%
            </p>
            <p className="text-sm font-medium text-green-700 mt-1">
              {t("nps.legend.promoters")}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {promoters} / {total}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("nps.segment.scoreRange9_10")}
            </p>
          </div>
        </div>
      </div>

      {/* Distribution détaillée */}
      <div className="p-4 bg-gray-50 rounded-lg" data-testid="nps-distribution">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          {t("nps.distribution.title")}
        </h4>
        <div className="space-y-2">
          {distribution.map(({ score, count, percent }) => (
            <div key={score} className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600 w-8">
                {score}
              </span>
              <div className="flex-1 h-6 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    score <= 6
                      ? "bg-red-500"
                      : score <= 8
                        ? "bg-yellow-500"
                        : "bg-green-500"
                  }`}
                  style={{ width: `${percent}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600 w-16 text-right">
                {count} ({percent}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Explication NPS */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">
          {t("nps.explanation.title")}
        </h4>
        <p className="text-xs text-blue-800 leading-relaxed">
          {t("nps.explanation.body")}
        </p>
      </div>
    </div>
  );
}
